/* Copyright (c) 2020-present CloudIO Inc */

import { getName } from '../../../registry/Manager';
import { Platform } from '../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './CardHeader';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { typographyVariant } from 'componentregistry/registry/constants';

const name = 'Card Header';

const CardHeaderManager: ComponentManagerFn<'CardHeader'> = (
  platform: Platform,
): ComponentManager<'CardHeader'> => {
  return {
    ...baseComponentManager<'CardHeader'>(platform, 'CardHeader'),
    defaultChildren: ['Avatar', 'IconButton'],
    onChildMoveIn: ({ page, value }) => {
      // Avatar must be the first child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (aItem.type === 'Avatar' && bItem.type !== 'Avatar') {
            return -1;
          }
          if (bItem.type === 'Avatar' && aItem.type !== 'Avatar') {
            return 1;
          }
        }
        return 0;
      });
    },
    isChildAllowed: (page, item, childType, { pageId, appUid }) => {
      if (
        !(
          [
            'ActionButton',
            'Avatar',
            'Badge',
            'Button',
            'ButtonDropdown',
            'ButtonGroup',
            'IconButton',
            'ProgressIndicator',
            'Toolbar',
            'GridPersonalizationMenu',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      if (childType === 'Avatar') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added to ${name}`;
      }
      return true;
    },
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Header',
      },
      titleVariant: {
        label: 'Title Variant',
        type: 'select',
        options: typographyVariant,
        category: 'Header',
      },
      titleFontSize: {
        label: 'Title Font Size',
        type: 'string',
        category: 'Header',
      },
      titleFontWeight: {
        label: 'Title Font Weigth',
        type: 'number',
        category: 'Header',
        min: 100,
        max: 900,
        step: 100,
      },
      titleFontFamily: {
        label: 'Title Font Family',
        type: 'string',
        category: 'Header',
      },
      titleFontStyle: {
        label: 'Title Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Header',
      },
      titleNoWrap: {
        label: 'Title No Wrap',
        type: 'boolean',
        helpText:
          'If checked, the text will not wrap, but instead will truncate with a text overflow ellipsis.',
        category: 'Header',
      },
      titleColor: {
        label: 'Title Color',
        type: 'ColorExpression',
        category: 'Header',
      },
      titleParagraph: {
        label: 'Title Paragraph',
        type: 'boolean',
        helpText: 'If checked, the text will have a bottom margin.',
        category: 'Header',
      },
      subheader: {
        label: 'Sub Title',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Sub Header',
      },
      subheaderVariant: {
        label: 'Subheader Variant',
        type: 'select',
        options: typographyVariant,
        category: 'Sub Header',
      },
      subheaderFontSize: {
        label: 'Subheader Font Size',
        type: 'string',
        category: 'Sub Header',
      },
      subheaderFontWeight: {
        label: 'Subheader Font Weigth',
        type: 'number',
        category: 'Sub Header',
        min: 100,
        max: 900,
        step: 100,
      },
      subheaderFontFamily: {
        label: 'Subheader Font Family',
        type: 'string',
        category: 'Sub Header',
      },
      subheaderFontStyle: {
        label: 'Subheader Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Sub Header',
      },
      subheaderNoWrap: {
        label: 'Subheader No Wrap',
        type: 'boolean',
        helpText:
          'If checked, the text will not wrap, but instead will truncate with a text overflow ellipsis.',
        category: 'Sub Header',
      },
      subheaderColor: {
        label: 'Subheader Color',
        type: 'ColorExpression',
        category: 'Sub Header',
      },
      subheaderParagraph: {
        label: 'Subheader Paragraph',
        type: 'boolean',
        helpText: 'If checked, the text will have a bottom margin.',
        category: 'Sub Header',
      },
      bgcolor: {
        label: 'Background Color',
        type: 'ColorExpression',
        helpText: 'e.g. divider or primary.light or any valid CSS color string',
        category: 'Look & Feel',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Look & Feel',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Look & Feel',
        helpText:
          'Select here to apply the borders.This border size will be included in height/width',
      },
    },
    getDisplayText: (item) => item.props.title.value,
    name,
    canHaveChildren: () => true,
    category: 'Containers',
    showEmptyPlaceholderInDesignerView: false,
    icon: { iconName: 'io-card-header', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Card Header serves as the visual anchor for the Card component. It is the dedicated top section used to establish context, displaying the primary title, subtitle, and essential quick actions.
By separating the introductory context from the main body content, the Card Header ensures that users can quickly scan dashboards or lists and understand the purpose of each card immediately.

**1. Text Hierarchy (Title & Subtitle)**<br/>
The core function of the header is to label the card. You can configure both a primary Title and a secondary Subtitle to establish a clear information hierarchy. Both elements share a robust set of typography controls:
* **Title / Sub Title:** Accepts static text or dynamic expressions (e.g., binding to a "Card Name" variable).
* **Variant:** Applies standard typographic themes (e.g., H1–H6, Body 1, Subtitle 1) to ensure consistency with the rest of your application's design system.
* **Font Color:** Customizes the text color. (Defaults to high-contrast dark gray for titles and lighter gray for subtitles).
* **No Wrap:** If enabled, long text will not break onto a new line. Instead, it will be truncated with an ellipsis (...). Use this to maintain strict vertical alignment in grid layouts.
* **Paragraph:** Adds a bottom margin to the text element, creating space between the header text and any content below it.
* **Advanced Typography Controls:** For granular control, you can override the standard variant settings using properties such as **Font Size**, **Font Weight**, **Font Family**, and **Font Style**.

**2. Visual Styling**<br/>
These settings define the container aesthetics of the header area, allowing it to stand out from or blend in with the rest of the card.
* **Background Color:** Sets the fill color of the header section. A common pattern is to give the header a distinct color (like a light gray or brand color) to visually separate it from the white Card Content body.
* **Padding:** Controls the internal spacing around the text and icons.
* **Border:** Defines the boundary lines around the header.

**3. Actions & Iconography**<br/>
The Card Header is also a container for interactive elements. It automatically manages the layout of specific child components to ensure a standard composition.
* **Avatar Integration:** If you add an Avatar component as a child, the Card Header will automatically position it as the first item (far left), typically used for user profile images or category icons.
* **Quick Actions:** You can add Icon Buttons, Menus, or Toolbars. These are automatically positioned to the right of the text, making them ideal for high-frequency actions like "Refresh," "Settings," or "More Options" (kebab menu).`,
  };
};

export default CardHeaderManager;
